import { z } from 'zod';
import { CursorPageQuery, IsoDate, IsoDateTime, Uuid, Version } from './common.js';
import { PeriodQuery, withOrderedPeriod } from './analytics.js';
import { ConsentChannel, ConsentState, CustomerKind } from './customers.js';
import { RevenueModule } from './insights.js';
/**
 * Horevia CRM (ADR-025) — connaître et fidéliser : vue 360 du client
 * (séjours + commandes, chaque vente une fois, R-CRM-02), programme de
 * fidélité (journal immuable + solde matérialisé, R-CRM-03 / R-CRM-04),
 * segments déclaratifs, listes d'action et relances individuelles sous
 * consentement (R-CRM-05), journal des interactions, doublons.
 * Routes `/crm/*` : module `crm` requis (R-CRM-07).
 */
// ---------------------------------------------------------------------------
// Réglages et modèles de message
// ---------------------------------------------------------------------------
export const CrmSettings = z.object({
    /** Un client sans visite depuis ce nombre de jours est « inactif ». */
    inactiveAfterDays: z.number().int(),
    /** Fenêtre des anniversaires à venir, en jours. */
    birthdayWindowDays: z.number().int(),
    version: Version,
    updatedAt: IsoDateTime.nullable(),
});
export const UpdateCrmSettingsRequest = z.object({
    inactiveAfterDays: z.number().int().min(7).max(3650),
    birthdayWindowDays: z.number().int().min(1).max(366),
    version: Version,
});
export const MessageChannel = z.enum(['EMAIL', 'WHATSAPP', 'SMS', 'ANY']);
/** Contexte d'une relance ou d'un contact : ce qui l'a motivé. */
export const InteractionContext = z.enum(['BIRTHDAY', 'INACTIVE', 'SEGMENT', 'MANUAL', 'SERVICE']);
/** Variables acceptées dans les modèles (`{{prenom}}`, …), rendues côté serveur. */
export const MESSAGE_TEMPLATE_VARIABLES = [
    'prenom',
    'nom',
    'nom_complet',
    'points',
    'palier',
    'etablissement',
    'derniere_visite',
];
export const MessageTemplate = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    channel: MessageChannel,
    context: InteractionContext,
    subject: z.string().nullable(),
    body: z.string(),
    isActive: z.boolean(),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CreateMessageTemplateRequest = z.object({
    code: z
        .string()
        .trim()
        .min(2)
        .max(40)
        .regex(/^[A-Za-z0-9_-]+$/, 'Lettres, chiffres, tiret et souligné uniquement'),
    name: z.string().trim().min(1).max(120),
    channel: MessageChannel.default('ANY'),
    context: InteractionContext.default('MANUAL'),
    subject: z.string().trim().max(200).nullable().optional(),
    body: z.string().trim().min(1).max(4000),
    isActive: z.boolean().default(true),
});
export const UpdateMessageTemplateRequest = CreateMessageTemplateRequest.partial().extend({
    version: Version,
});
// ---------------------------------------------------------------------------
// Fidélité
// ---------------------------------------------------------------------------
export const LoyaltyTierBasis = z.enum(['LIFETIME', 'ROLLING']);
export const LoyaltyTier = z.object({
    id: Uuid,
    code: z.string(),
    name: z.string(),
    minPoints: z.number().int(),
    benefits: z.array(z.string()),
    sortOrder: z.number().int(),
});
export const LoyaltyTierInput = z.object({
    code: z
        .string()
        .trim()
        .min(1)
        .max(30)
        .regex(/^[A-Za-z0-9_-]+$/, 'Lettres, chiffres, tiret et souligné uniquement'),
    name: z.string().trim().min(1).max(80),
    minPoints: z.number().int().min(0),
    benefits: z.array(z.string().trim().min(1).max(160)).max(12).default([]),
});
export const LoyaltyProgram = z.object({
    id: Uuid,
    name: z.string(),
    isActive: z.boolean(),
    startsOn: IsoDate.nullable(),
    /** `pointsPerStep` points par tranche entière de `stepAmount` (devise du tenant). */
    pointsPerStep: z.number().int(),
    stepAmount: z.number(),
    /** Valeur d'un point à l'utilisation, devise du tenant. */
    pointValue: z.number(),
    earnOnHotel: z.boolean(),
    earnOnPos: z.boolean(),
    includeCompanies: z.boolean(),
    tierBasis: LoyaltyTierBasis,
    tierWindowMonths: z.number().int(),
    tiers: z.array(LoyaltyTier),
    currencyCode: z.string().length(3),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
/** Création ou remplacement du programme (un seul par tenant) ; `version` absente à la création. */
export const UpsertLoyaltyProgramRequest = z
    .object({
    name: z.string().trim().min(1).max(120),
    isActive: z.boolean().default(false),
    startsOn: IsoDate.nullable().optional(),
    pointsPerStep: z.number().int().min(1).max(100000),
    stepAmount: z.number().positive(),
    pointValue: z.number().nonnegative().default(0),
    earnOnHotel: z.boolean().default(true),
    earnOnPos: z.boolean().default(true),
    includeCompanies: z.boolean().default(false),
    tierBasis: LoyaltyTierBasis.default('ROLLING'),
    tierWindowMonths: z.number().int().min(1).max(120).default(12),
    tiers: z.array(LoyaltyTierInput).max(10).default([]),
    version: Version.optional(),
})
    .superRefine((input, ctx) => {
    const codes = new Set();
    input.tiers.forEach((tier, index) => {
        const code = tier.code.toLowerCase();
        if (codes.has(code)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['tiers', index, 'code'],
                message: 'Code de palier en double',
            });
        }
        codes.add(code);
    });
});
export const LoyaltyTransactionType = z.enum(['EARN', 'REDEEM', 'ADJUST', 'REVERSAL']);
export const LoyaltySourceType = z.enum(['order', 'reservation', 'manual', 'merge']);
export const LoyaltyTransaction = z.object({
    id: Uuid,
    customerId: Uuid,
    type: LoyaltyTransactionType,
    /** Signé : positif pour une acquisition ou un crédit, négatif pour une utilisation ou un débit. */
    points: z.number().int(),
    amount: z.number().nullable(),
    currencyCode: z.string().length(3).nullable(),
    sourceType: LoyaltySourceType,
    sourceId: Uuid.nullable(),
    /** Référence lisible de la source (commande, séjour), résolue à la lecture. */
    sourceReference: z.string().nullable(),
    reason: z.string().nullable(),
    occurredAt: IsoDateTime,
    createdByName: z.string().nullable(),
});
export const LoyaltyTierSummary = LoyaltyTier.pick({
    id: true,
    code: true,
    name: true,
    minPoints: true,
    benefits: true,
});
export const LoyaltyAccount = z.object({
    customerId: Uuid,
    balance: z.number().int(),
    earnedTotal: z.number().int(),
    redeemedTotal: z.number().int(),
    /** Points comptés pour le palier (à vie ou sur la fenêtre glissante). */
    tierPoints: z.number().int(),
    tier: LoyaltyTierSummary.nullable(),
    nextTier: LoyaltyTierSummary.extend({ missingPoints: z.number().int() }).nullable(),
    /** Valeur d'un point à l'utilisation et valeur du solde, devise du tenant. */
    pointValue: z.number(),
    balanceValue: z.number(),
    currencyCode: z.string().length(3),
    programActive: z.boolean(),
});
export const LoyaltyAdjustmentRequest = z.object({
    customerId: Uuid,
    /** Signé, non nul : crédit ou débit manuel motivé (`crm.manage`). */
    points: z
        .number()
        .int()
        .min(-1_000_000)
        .max(1_000_000)
        .refine((value) => value !== 0, 'Le nombre de points ne peut pas être nul'),
    reason: z.string().trim().min(5).max(500),
});
/**
 * Utilisation de points (`crm.loyalty.redeem`, R-CRM-04) : sur une commande
 * ouverte sans remise, pose la remise « Fidélité » (`orderId` + `version` de
 * la commande) ; sur un séjour ou sans cible, enregistre le débit motivé.
 */
export const LoyaltyRedeemRequest = z
    .object({
    customerId: Uuid,
    points: z.number().int().min(1).max(1_000_000),
    orderId: Uuid.optional(),
    reservationId: Uuid.optional(),
    /** Version de la commande (verrou optimiste) ; requise avec `orderId`. */
    version: Version.optional(),
    reason: z.string().trim().max(500).nullable().optional(),
})
    .superRefine((input, ctx) => {
    if (input.orderId && input.reservationId) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['reservationId'],
            message: 'Une seule cible : commande ou séjour',
        });
    }
    if (input.orderId && input.version === undefined) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['version'],
            message: 'La version de la commande est requise',
        });
    }
});
export const LoyaltyRedeemResult = z.object({
    account: LoyaltyAccount,
    transaction: LoyaltyTransaction,
    /** Montant de la remise posée sur la commande, `null` sans commande. */
    discountAmount: z.number().nullable(),
});
export const ListLoyaltyTransactionsQuery = CursorPageQuery;
// ---------------------------------------------------------------------------
// Interactions et messages
// ---------------------------------------------------------------------------
export const InteractionKind = z.enum([
    'CALL',
    'WHATSAPP',
    'EMAIL',
    'SMS',
    'VISIT',
    'NOTE',
    'OTHER',
]);
export const CustomerInteraction = z.object({
    id: Uuid,
    customerId: Uuid,
    kind: InteractionKind,
    context: InteractionContext,
    subject: z.string().nullable(),
    body: z.string().nullable(),
    outcome: z.string().nullable(),
    occurredAt: IsoDateTime,
    userName: z.string().nullable(),
});
export const CreateInteractionRequest = z.object({
    kind: InteractionKind,
    context: InteractionContext.default('MANUAL'),
    subject: z.string().trim().max(200).nullable().optional(),
    body: z.string().trim().max(4000).nullable().optional(),
    outcome: z.string().trim().max(200).nullable().optional(),
    occurredAt: IsoDateTime.optional(),
});
export const ListInteractionsQuery = CursorPageQuery;
/** Envoi individuel par e-mail (R-CRM-05) : consentement vérifié côté serveur. */
export const SendCustomerMessageRequest = z
    .object({
    channel: z.literal('EMAIL').default('EMAIL'),
    context: InteractionContext.default('MANUAL'),
    templateId: Uuid.optional(),
    subject: z.string().trim().max(200).optional(),
    body: z.string().trim().max(4000).optional(),
})
    .superRefine((input, ctx) => {
    if (!input.templateId && !input.body) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['body'],
            message: 'Un modèle ou un texte est requis',
        });
    }
});
export const SendCustomerMessageResult = z.object({
    sent: z.boolean(),
    to: z.string(),
    interaction: CustomerInteraction,
});
// ---------------------------------------------------------------------------
// Segments
// ---------------------------------------------------------------------------
export const ActivityModule = z.enum(['hotel', 'pos']);
/** Critères déclaratifs d'un segment (D-H65), tous facultatifs et cumulatifs. */
export const SegmentCriteria = z.object({
    kinds: z.array(CustomerKind).max(2).optional(),
    tags: z.array(z.string().trim().min(1).max(40)).max(20).optional(),
    tierCodes: z.array(z.string().trim().min(1).max(30)).max(10).optional(),
    /** Visites (séjours + commandes) et dépense sur les `periodMonths` derniers mois. */
    minVisits: z.number().int().min(1).optional(),
    minSpend: z.number().nonnegative().optional(),
    periodMonths: z.number().int().min(1).max(120).default(12),
    lastVisitBefore: IsoDate.optional(),
    lastVisitAfter: IsoDate.optional(),
    /** Aucune visite depuis N jours. */
    inactiveDays: z.number().int().min(1).optional(),
    /** Mois d'anniversaire (1 → 12). */
    birthdayMonths: z.array(z.number().int().min(1).max(12)).max(12).optional(),
    consent: z.object({ channel: ConsentChannel, granted: z.boolean() }).optional(),
    /** Modules fréquentés : au moins un séjour (`hotel`) ou une commande (`pos`). */
    modules: z.array(ActivityModule).max(2).optional(),
    outletIds: z.array(Uuid).max(20).optional(),
    propertyId: Uuid.optional(),
});
export const CrmSegment = z.object({
    id: Uuid,
    name: z.string(),
    description: z.string().nullable(),
    criteria: SegmentCriteria,
    isActive: z.boolean(),
    /** Effectif calculé à la lecture. */
    memberCount: z.number().int(),
    version: Version,
    createdAt: IsoDateTime,
    updatedAt: IsoDateTime,
});
export const CreateSegmentRequest = z.object({
    name: z.string().trim().min(1).max(120),
    description: z.string().trim().max(500).nullable().optional(),
    criteria: SegmentCriteria,
    isActive: z.boolean().default(true),
});
export const UpdateSegmentRequest = CreateSegmentRequest.partial().extend({ version: Version });
export const SegmentPreviewRequest = z.object({ criteria: SegmentCriteria });
export const SegmentPreview = z.object({ count: z.number().int() });
// ---------------------------------------------------------------------------
// Vue 360
// ---------------------------------------------------------------------------
export const CustomerActivity = z.enum(['NEW', 'ACTIVE', 'INACTIVE', 'NONE']);
export const SpendByModuleRow = z.object({ module: RevenueModule, amount: z.number() });
export const CrmLoyaltySummary = z.object({
    balance: z.number().int(),
    tierCode: z.string().nullable(),
    tierName: z.string().nullable(),
});
export const CrmCustomerRow = z.object({
    id: Uuid,
    kind: CustomerKind,
    displayName: z.string(),
    phonePrimary: z.string().nullable(),
    email: z.string().nullable(),
    tags: z.array(z.string()),
    /** Séjours (hébergé, ou payeur pour une entreprise) et commandes rattachées, à vie. */
    staysCount: z.number().int(),
    ordersCount: z.number().int(),
    visits: z.number().int(),
    spendTotal: z.number(),
    spendByModule: z.array(SpendByModuleRow),
    firstVisitAt: IsoDateTime.nullable(),
    lastVisitAt: IsoDateTime.nullable(),
    activity: CustomerActivity,
    birthDate: IsoDate.nullable(),
    loyalty: CrmLoyaltySummary.nullable(),
    /** Canaux dont le consentement marketing est accordé. */
    grantedChannels: z.array(ConsentChannel),
    createdAt: IsoDateTime,
});
export const CrmCustomerSort = z.enum(['spend', 'lastVisit', 'visits', 'name', 'points']);
export const ListCrmCustomersQuery = CursorPageQuery.extend({
    search: z.string().trim().max(100).optional(),
    segmentId: Uuid.optional(),
    tierCode: z.string().trim().max(30).optional(),
    activity: CustomerActivity.optional(),
    kind: CustomerKind.optional(),
    propertyId: Uuid.optional(),
    sort: CrmCustomerSort.default('spend'),
});
export const CrmStayRow = z.object({
    id: Uuid,
    reference: z.string(),
    status: z.string(),
    resourceName: z.string().nullable(),
    arrivalAt: IsoDateTime,
    departureAt: IsoDateTime,
    nights: z.number().int(),
    total: z.number(),
});
export const CrmOrderRow = z.object({
    id: Uuid,
    reference: z.string(),
    status: z.string(),
    outletName: z.string(),
    serviceDate: IsoDate,
    closedAt: IsoDateTime.nullable(),
    total: z.number(),
});
export const CrmCustomerDetail = z.object({
    customer: CrmCustomerRow,
    currencyCode: z.string().length(3),
    spendByYear: z.array(z.object({ year: z.number().int(), amount: z.number() })),
    /** Ticket moyen des commandes rattachées et valeur moyenne d'un séjour. */
    averageTicket: z.number().nullable(),
    averageStayValue: z.number().nullable(),
    favoriteOutlet: z
        .object({ outletId: Uuid, name: z.string(), ordersCount: z.number().int() })
        .nullable(),
    topProducts: z.array(z.object({
        productId: Uuid.nullable(),
        label: z.string(),
        quantity: z.number(),
        amount: z.number(),
    })),
    recentStays: z.array(CrmStayRow),
    recentOrders: z.array(CrmOrderRow),
    loyalty: LoyaltyAccount.nullable(),
    recentTransactions: z.array(LoyaltyTransaction),
    consents: z.array(ConsentState),
    recentInteractions: z.array(CustomerInteraction),
});
export const TimelineEntryKind = z.enum([
    'STAY',
    'ORDER',
    'PAYMENT',
    'LOYALTY',
    'INTERACTION',
    'CONSENT',
]);
export const TimelineEntry = z.object({
    id: z.string(),
    kind: TimelineEntryKind,
    occurredAt: IsoDateTime,
    title: z.string(),
    subtitle: z.string().nullable(),
    amount: z.number().nullable(),
    points: z.number().int().nullable(),
    reference: z.string().nullable(),
    entityId: Uuid.nullable(),
});
export const TimelineQuery = CursorPageQuery.extend({
    kinds: z.string().max(80).optional(),
});
// ---------------------------------------------------------------------------
// Vue d'ensemble
// ---------------------------------------------------------------------------
export const CrmOverviewQuery = PeriodQuery;
export const CrmOverviewQueryStrict = withOrderedPeriod(PeriodQuery);
export const CrmOverview = z.object({
    period: z.object({ from: IsoDate, to: IsoDate }),
    currencyCode: z.string().length(3),
    customers: z.object({
        /** Fiches actives (non archivées). */
        known: z.number().int(),
        /** Au moins une visite sur la période. */
        active: z.number().int(),
        /** Première visite sur la période. */
        new: z.number().int(),
        /** Actifs sur la période qui avaient déjà une visite avant. */
        returning: z.number().int(),
        /** Part des ventes POS de la période rattachées à un client (0 → 100), `null` sans `pos`. */
        identifiedOrdersShare: z.number().nullable(),
    }),
    spendByModule: z.array(SpendByModuleRow),
    topCustomers: z.array(z.object({
        id: Uuid,
        displayName: z.string(),
        kind: CustomerKind,
        spend: z.number(),
        visits: z.number().int(),
    })),
    birthdays: z.object({ count: z.number().int(), windowDays: z.number().int() }),
    inactive: z.object({ count: z.number().int(), afterDays: z.number().int() }),
    consents: z.array(z.object({ channel: ConsentChannel, granted: z.number().int() })),
    loyalty: z
        .object({
        programName: z.string(),
        isActive: z.boolean(),
        members: z.number().int(),
        earned: z.number().int(),
        redeemed: z.number().int(),
        byTier: z.array(z.object({ code: z.string(), name: z.string(), count: z.number().int() })),
    })
        .nullable(),
});
// ---------------------------------------------------------------------------
// Listes d'action (relances)
// ---------------------------------------------------------------------------
export const FollowUpKind = z.enum(['BIRTHDAYS', 'INACTIVE', 'NO_CONSENT', 'SEGMENT']);
export const FollowUpsQuery = z.object({
    kind: FollowUpKind,
    segmentId: Uuid.optional(),
    propertyId: Uuid.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const FollowUpRow = z.object({
    customer: z.object({
        id: Uuid,
        displayName: z.string(),
        kind: CustomerKind,
        phonePrimary: z.string().nullable(),
        email: z.string().nullable(),
    }),
    /** Date qui motive la ligne : anniversaire à venir ou dernière visite. */
    date: IsoDate.nullable(),
    lastVisitAt: IsoDateTime.nullable(),
    spendTotal: z.number(),
    loyalty: CrmLoyaltySummary.nullable(),
    consents: z.array(ConsentState),
    lastInteractionAt: IsoDateTime.nullable(),
    suggestedMessage: z
        .object({
        templateId: Uuid,
        channel: MessageChannel,
        subject: z.string().nullable(),
        body: z.string(),
    })
        .nullable(),
});
export const FollowUps = z.object({
    kind: FollowUpKind,
    rows: z.array(FollowUpRow),
    total: z.number().int(),
});
// ---------------------------------------------------------------------------
// Doublons
// ---------------------------------------------------------------------------
export const DuplicateCriterion = z.enum(['PHONE', 'EMAIL', 'NAME_BIRTHDATE']);
export const DuplicateGroup = z.object({
    criterion: DuplicateCriterion,
    value: z.string(),
    customers: z.array(z.object({
        id: Uuid,
        displayName: z.string(),
        kind: CustomerKind,
        phonePrimary: z.string().nullable(),
        email: z.string().nullable(),
        visits: z.number().int(),
        createdAt: IsoDateTime,
    })),
});
//# sourceMappingURL=crm.js.map